import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { memo } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';

import { useJobItemVersions } from '../api/use-job-item-versions';

type StampKind = 'reviewed' | 'published';

type StampProps = {
    readonly date: string;
    readonly kind: StampKind;
    readonly localeCode: string;
    readonly muted: boolean;
    readonly version: number;
};

const dateFormatters = new Map<string, Intl.DateTimeFormat>();

const safeDateFormatter = (localeCode: string): Intl.DateTimeFormat => {
    const cached = dateFormatters.get(localeCode);
    if (!is.undefined(cached)) {
        return cached;
    }
    let formatter: Intl.DateTimeFormat;
    try {
        formatter = new Intl.DateTimeFormat(localeCode, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        formatter = new Intl.DateTimeFormat('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    }
    dateFormatters.set(localeCode, formatter);
    return formatter;
};

const formatDate = (iso: string, localeCode: string): string => {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
        return '—';
    }
    return safeDateFormatter(localeCode).format(parsed);
};

const isoDateAttribute = (iso: string): string => {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
        return iso;
    }
    return parsed.toISOString().slice(0, 10);
};

const Stamp: FunctionComponent<StampProps> = ({ date, kind, localeCode, muted, version }) => {
    const translate = useTranslate();
    const baseClassName =
        'flex flex-col items-start rounded-[10px] border px-[12px] py-[9px] transition-colors duration-200 ease-out';
    const crispClassName =
        kind === 'reviewed'
            ? 'border-[rgba(0,41,132,0.5)] bg-[rgba(232,240,252,0.78)] text-[#002984]'
            : 'border-[rgba(22,101,52,0.5)] bg-[rgba(232,247,237,0.72)] text-[#166534]';
    const mutedClassName = 'border-gray-300 border-dashed bg-[rgba(245,245,245,0.85)] text-gray-500 opacity-55';
    const className = `${baseClassName} ${muted ? mutedClassName : crispClassName}`;
    const keywordKey =
        kind === 'reviewed'
            ? 'pages.job-architecture-v2.job-profile-status-stamp.reviewed'
            : 'pages.job-architecture-v2.job-profile-status-stamp.published';
    const formattedDate = formatDate(date, localeCode);

    return (
        <span
            aria-hidden={true}
            className={className}
            data-testid={
                kind === 'reviewed' ? 'job-profile-status-stamp-reviewed' : 'job-profile-status-stamp-published'
            }
            data-state={muted ? 'muted' : 'crisp'}
        >
            <span className="text-xs font-bold uppercase leading-none tracking-[0.08em]">{translate(keywordKey)}</span>
            <span className="mt-xs whitespace-nowrap text-[0.6875rem] font-medium leading-tight opacity-70">
                {translate('pages.job-architecture-v2.job-profile-status-stamp.version-prefix')} {version} ·{' '}
                <time dateTime={isoDateAttribute(date)}>{formattedDate}</time>
            </span>
        </span>
    );
};

type DatedField = 'reviewedAt' | 'publishedAt';
type VersionWithDate<F extends DatedField> = JobProfileVersion & {
    [K in F]: string;
};

const findHighest = <F extends DatedField>(
    versions: readonly JobProfileVersion[],
    field: F,
): VersionWithDate<F> | undefined => {
    return versions
        .filter((v): v is VersionWithDate<F> => !is.nullOrUndefined(v[field]))
        .toSorted((a, b) => b.version - a.version)
        .at(0);
};

type JobProfileStatusStampsProps = {
    readonly architectureId: string;
    readonly currentVersion: number;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
};

export const JobProfileStatusStamps: FunctionComponent<JobProfileStatusStampsProps> = memo(
    function JobProfileStatusStamps({ architectureId, currentVersion, itemId, itemType }) {
        const { applicationState } = useApplicationContext();
        const isFFOn =
            itemType === 'job-role' || applicationState.featureFlagService.isFeatureFlagEnabled('review-status-table');
        const { data: versions } = useJobItemVersions({
            architectureId,
            itemId,
            itemType,
        });

        if (!isFFOn || is.nullOrUndefined(versions) || is.emptyArray(versions)) {
            return null;
        }

        const highestReviewed = findHighest(versions, 'reviewedAt');
        const highestPublished = findHighest(versions, 'publishedAt');

        if (is.nullOrUndefined(highestReviewed) && is.nullOrUndefined(highestPublished)) {
            return null;
        }

        const localeCode = applicationState.language.code;

        return (
            <div className="flex shrink-0 items-center gap-[10px]" data-testid="job-profile-status-stamps">
                {!is.nullOrUndefined(highestReviewed) ? (
                    <Stamp
                        date={highestReviewed.reviewedAt}
                        kind="reviewed"
                        localeCode={localeCode}
                        muted={highestReviewed.version !== currentVersion}
                        version={highestReviewed.version}
                    />
                ) : null}
                {!is.nullOrUndefined(highestPublished) ? (
                    <Stamp
                        date={highestPublished.publishedAt}
                        kind="published"
                        localeCode={localeCode}
                        muted={highestPublished.version !== currentVersion}
                        version={highestPublished.version}
                    />
                ) : null}
            </div>
        );
    },
);
